// Etichette del modulo Circolari usate in tutto il sito (admin e area riservata):
// numero con suffisso, categoria dell'archivio, tipo degli allegati e limiti di
// caricamento. Rispecchiano il backend (models/Circolare.js, utils/circolariFiles.js):
// se cambiano lì vanno aggiornate qui. L'app ha la sua copia in
// mobile/src/features/circolari/stato.ts.

/** Sigla del registro di protocollo della Segreteria Nazionale: NON decide chi vede la circolare. */
export type SuffissoCircolare = 'SN' | 'SNA' | 'SNS';
export const SUFFISSI: SuffissoCircolare[] = ['SN', 'SNA', 'SNS'];

/**
 * Campo `categoria` della circolare (le cinque voci del vecchio sito). Da non
 * confondere con le categorie della visibilità (STUDIO, AUTOSCUOLA, NAUTICA).
 */
export type CategoriaArchivio = 'circolari_norme' | 'comunicati' | 'sted' | 'documento_unico' | 'materiale_associativo';

export const CATEGORIA_PREDEFINITA: CategoriaArchivio = 'circolari_norme';

export const CATEGORIE_ARCHIVIO: { value: CategoriaArchivio; label: string }[] = [
  { value: 'circolari_norme', label: 'Circolari e norme' },
  { value: 'comunicati', label: 'Comunicati' },
  { value: 'sted', label: 'STED' },
  { value: 'documento_unico', label: 'Documento Unico' },
  { value: 'materiale_associativo', label: 'Materiale associativo' },
];

/** Etichetta leggibile; una circolare senza categoria vale come «Circolari e norme», come sul server. */
export const etichettaCategoria = (valore?: string | null): string => {
  const v = valore || CATEGORIA_PREDEFINITA;
  return CATEGORIE_ARCHIVIO.find(c => c.value === v)?.label || v;
};

export interface NumeroCircolare {
  numero?: number | null;
  suffisso?: string | null;
  anno: number;
  /** Protocollo dell'ente emittente: cita le voci senza numero UNASCA. */
  protocollo?: string | null;
  enteEmittente?: string | null;
}

const haNumero = (c: NumeroCircolare): boolean => typeof c.numero === 'number' && Number.isFinite(c.numero) && c.numero >= 1;

/** «479/SN/2026», «12/2026»; stringa vuota per le voci storiche senza numero. */
export const numeroBreve = (c: NumeroCircolare): string => {
  if (!haNumero(c)) return '';
  return c.suffisso ? `${c.numero}/${c.suffisso}/${c.anno}` : `${c.numero}/${c.anno}`;
};

/**
 * La stessa dicitura ovunque (e nei titoli di mail e notifiche, backend
 * models/Circolare.js): «n. 479/SN/2026», «n. 12/2026»; senza numero il
 * protocollo dell'ente, «prot. 16883» o «prot. 16883 · ACI-PRA»; senza nessuno
 * dei due «senza numero».
 */
export const etichettaNumero = (c: NumeroCircolare): string => {
  const breve = numeroBreve(c);
  if (breve) return `n. ${breve}`;
  const protocollo = (c.protocollo || '').trim();
  if (!protocollo) return 'senza numero';
  const ente = (c.enteEmittente || '').trim();
  return ente ? `prot. ${protocollo} · ${ente}` : `prot. ${protocollo}`;
};

/**
 * Anno con cui la circolare sta negli elenchi: quello della data della circolare
 * (lo stesso del filtro «anno» e di /anni sul server), non il campo `anno` del
 * protocollo UNASCA. Senza data valida ripiega su `anno`.
 */
export const annoElenco = (c: { anno: number; dataCircolare?: string | null }): number => {
  const t = c.dataCircolare ? new Date(c.dataCircolare) : null;
  return t && !isNaN(t.getTime()) ? t.getFullYear() : c.anno;
};

/* ---------- Allegati ---------- */

export const MIME_PDF = 'application/pdf';
export const MIME_DOCX = 'application/vnd.openxmlformats-officedocument.wordprocessingml.document';
export const MIME_XLSX = 'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet';

/** Valore dell'attributo `accept` del campo di caricamento. */
export const ACCETTA_ALLEGATI = '.pdf,.docx,.xlsx';
export const MAX_ALLEGATI = 25;
/** File per singolo caricamento (multer del server: MAX_FILES_PER_RICHIESTA) */
export const MAX_ALLEGATI_PER_VOLTA = 5;
export const MAX_ALLEGATO_MB = 25;
export const MAX_ALLEGATO_BYTES = MAX_ALLEGATO_MB * 1024 * 1024;

export type TipoAllegato = 'pdf' | 'word' | 'excel';

export interface InfoTipoAllegato {
  tipo: TipoAllegato;
  /** «PDF», «Word», «Excel» */
  etichetta: string;
  /** Il PDF si apre nel browser; Word ed Excel si scaricano col nome originale */
  azione: 'apri' | 'scarica';
}

const INFO: Record<TipoAllegato, InfoTipoAllegato> = {
  pdf: { tipo: 'pdf', etichetta: 'PDF', azione: 'apri' },
  word: { tipo: 'word', etichetta: 'Word', azione: 'scarica' },
  excel: { tipo: 'excel', etichetta: 'Excel', azione: 'scarica' },
};

const estensione = (nome?: string | null): string => {
  const m = /\.([a-z0-9]+)$/i.exec(nome || '');
  return m ? m[1].toLowerCase() : '';
};

/**
 * Tipo dell'allegato: dal `mimeType` salvato dal server, altrimenti
 * dall'estensione del nome. Senza indizi vale PDF (le circolari nate prima
 * di Word ed Excel hanno solo PDF).
 */
export const tipoAllegato = (a: { mimeType?: string | null; filename?: string | null; originalName?: string | null }): InfoTipoAllegato => {
  const mime = (a.mimeType || '').toLowerCase();
  if (mime === MIME_DOCX) return INFO.word;
  if (mime === MIME_XLSX) return INFO.excel;
  if (mime === MIME_PDF) return INFO.pdf;
  const ext = estensione(a.filename) || estensione(a.originalName);
  if (ext === 'docx') return INFO.word;
  if (ext === 'xlsx') return INFO.excel;
  return INFO.pdf;
};

/** true per .pdf, .docx, .xlsx (il server poi controlla il contenuto vero del file). */
export const estensioneAmmessa = (nome: string): boolean => ['pdf', 'docx', 'xlsx'].includes(estensione(nome));
